import React, {forwardRef, useImperativeHandle, useRef} from 'react';
import { Button, Checkbox, Form, InputNumber, Typography } from 'antd';
import { HeartOutlined } from '@ant-design/icons';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { applyLang } from '../layout';
import { computeReducedPrice, useFormPath } from './index';
import { quoteCustomPrice, customPriceModules } from './customPriceRules';

export function customPriceErrorMessage(code) {
    switch (code) {
        case 'CUSTOM_PRICE_TOO_LOW': return t`Le montant est inférieur à la participation minimum.`;
        case 'CUSTOM_PRICE_TOO_HIGH': return t`Le montant ne peut pas dépasser le tarif habituel.`;
        case 'CUSTOM_PRICE_ZERO': return t`La participation gratuite n'est pas autorisée pour ce formulaire.`;
        case 'CUSTOM_PRICE_RECONFIRM': return t`Votre sélection ou le tarif a changé. Vérifiez puis confirmez votre participation.`;
        case 'CUSTOM_PRICE_NO_PRODUCTS': return t`Sélectionnez un article payant avant de personnaliser votre participation.`;
        case 'CUSTOM_PRICE_DISABLED': return t`Le prix personnalisé n'est plus disponible. Revenez au tarif habituel.`;
        case 'CUSTOM_PRICE_DUPLICATE': return t`Ajoutez un seul module Prix personnalisé par formulaire.`;
        case 'CUSTOM_PRICE_MONTHLY': return t`Le prix personnalisé est disponible uniquement pour les paiements ponctuels.`;
        case 'CUSTOM_PRICE_PLACEMENT': return t`Placez le prix personnalisé dans le formulaire principal, en dehors des blocs conditionnels et répétés.`;
        default: return t`Saisissez un montant valide, avec deux décimales maximum.`;
    }
}

const ParticipationInput = forwardRef(function ParticipationInput({id, value = {}, onChange, quote, formPlugin, message, 'aria-invalid':invalid, 'aria-describedby':errorHelp}, ref) {
    const inputRef = useRef(null);
    const checkboxRef = useRef(null);
    useImperativeHandle(ref, () => ({focus:() => (value.enabled ? inputRef.current : checkboxRef.current)?.focus()}), [value.enabled]);
    const format = cents => formPlugin.currencyFormat(cents / 100);
    const enabled = value.enabled === true;
    const stale = enabled && value.referenceKey !== quote.referenceKey;
    const amountId = `${id || 'custom-price'}_amount`;
    return (
        <div id={id} className="form-custom-price" style={{display:'grid', gap:16, minWidth:0}}>
            <Typography.Paragraph style={{margin:0}}>{message || t`Nous souhaitons que chacun puisse participer selon ses moyens. Vous pouvez adapter votre participation.`}</Typography.Paragraph>
            <div><Trans>Tarif habituel de votre inscription</Trans> : <strong>{format(quote.referenceProducts)}</strong></div>
            <Checkbox ref={checkboxRef} checked={enabled} disabled={quote.referenceProducts <= 0}
                onChange={event => onChange({...value, enabled:event.target.checked, amount:value.amount ?? quote.referenceProducts / 100, referenceKey:quote.referenceKey})}>
                <Trans>Je souhaite adapter ma participation à mes moyens</Trans>
            </Checkbox>
            {enabled && <>
                <div style={{display:'grid', gap:8}}>
                    <label htmlFor={amountId}><Trans>Votre participation</Trans></label>
                    <InputNumber ref={inputRef} id={amountId} value={value.amount} changeOnBlur={false}
                        inputMode="decimal" decimalSeparator="," addonAfter={formPlugin.getDeviseSymbol()} style={{width:'100%', maxWidth:320}}
                        aria-valuemin={quote.config.allowZero ? 0 : quote.minimumCents / 100}
                        aria-valuemax={quote.config.allowHigher ? undefined : quote.referenceProducts / 100}
                        aria-invalid={invalid} aria-describedby={[`${amountId}_help`,errorHelp].filter(Boolean).join(' ')}
                        onChange={amount => onChange({...value, amount, referenceKey:stale ? value.referenceKey : quote.referenceKey})}/>
                    <Typography.Text id={`${amountId}_help`}>
                        <Trans>Participation minimum</Trans> : {format(quote.minimumCents)}.
                        {quote.config.allowZero && <> <Trans>Vous pouvez également choisir 0.</Trans></>}
                        {!quote.config.allowHigher && <> <Trans>Maximum</Trans> : {format(quote.referenceProducts)}.</>}
                    </Typography.Text>
                </div>
                {stale && <div role="status" style={{display:'grid', gap:8}}>
                    {!invalid && <Typography.Text>{customPriceErrorMessage('CUSTOM_PRICE_RECONFIRM')}</Typography.Text>}
                    <Button onClick={() => onChange({...value, referenceKey:quote.referenceKey})} style={{justifySelf:'start', whiteSpace:'normal', height:'auto'}}>
                        <Trans>Confirmer ce montant</Trans>
                    </Button>
                </div>}
                <Button type="link" onClick={() => onChange({enabled:false})} style={{padding:0, justifySelf:'start', whiteSpace:'normal', height:'auto'}}>
                    <Trans>Revenir au tarif habituel</Trans>
                </Button>
            </>}
            {quote.donationTotal > 0 && <Typography.Text><Trans>Votre don supplémentaire reste ajouté séparément.</Trans></Typography.Text>}
        </div>
    );
});

export default (formPlugin, namespace) => ({
    Renderer: function CustomPriceRenderer(props) {
        const {path} = useFormPath();
        if (props.remove) formPlugin.onAddFormPlugin(props, props.nodeId, props.pluginConfig.id);
        if (namespace !== '/' || path?.length || formPlugin.formData?.type !== 'COMPLEX' || !formPlugin.formData?.withPaiement || ['MONTHLY','ABONNEMENT'].includes(formPlugin.formData?.paiementType)) return null;
        const data = applyLang(props.data || {}, {value:props.lang});
        // Numeric constraints always come from the source language, never a translation.
        const pricingForm = {...formPlugin.formData, simpleEditor:true, modulesContent:[{type:'CUSTOM-PRICE', data:props.data || {}}]};
        const quoteFor = values => quoteCustomPrice({form:pricingForm, values, devise:formPlugin.org?.devise, baseDevise:formPlugin.formData?.devise || formPlugin.org?.devise, productPrice:computeReducedPrice});
        return <Form.Item shouldUpdate noStyle>{form => {
            const quote = quoteFor(form.getFieldsValue(true));
            if (!quote || !Number.isFinite(quote.referenceProducts)) return null;
            return <Form.Item name="__customPrice" label={data.label || t`Votre participation`}
                rules={[{validator:(_, value) => {
                    const result = quoteFor({...form.getFieldsValue(true), __customPrice:value});
                    return result?.error ? Promise.reject(new Error(customPriceErrorMessage(result.error))) : Promise.resolve();
                }}]}>
                <ParticipationInput quote={quote} formPlugin={formPlugin} message={data.message}/>
            </Form.Item>;
        }}</Form.Item>;
    },
    namespace,
    id:'form-custom-price' + namespace,
    title:t`Prix personnalisé`,
    description:t`Permettre aux participants d'adapter le prix de leur inscription à leurs moyens.`,
    icon:<HeartOutlined/>,
    version:1,
    hideInMenu:namespace !== '/' || formPlugin.formData?.type !== 'COMPLEX' || !formPlugin.formData?.withPaiement || ['MONTHLY','ABONNEMENT'].includes(formPlugin.formData?.paiementType) || customPriceModules({...formPlugin.formData,simpleEditor:false,content:formPlugin.savedTree}).length > 0,
    controls:{type:'autoform', schema:{properties:{
        label:{type:'string', uniforms:{label:t`Titre du module`, placeholder:t`Votre participation`}},
        message:{type:'string', uniforms:{label:t`Message aux participants`, multiline:true, rows:3}},
        minimumAmount:{type:'number', minimum:0, default:1, uniforms:{label:t`Participation minimum (dans la devise affichée)`}},
        allowHigher:{type:'boolean', default:false, uniforms:{label:t`Autoriser une participation supérieure au tarif habituel`}},
        allowZero:{type:'boolean', default:false, uniforms:{label:t`Autoriser la gratuité (0)`}},
    }}},
});
